import React, { useCallback, useEffect, useMemo, useState } from "react"
import { matchPath, useLocation } from "react-router-dom"
import { LicensesSet, User } from "../../../../mp-database"
import { Roles } from "../../../../mp-utils/generic-utils"
import getHomePath from "../../../../mp-utils/ready-utils/getHomePath"
import {
  CURRENT_PROGRAM_KEY,
  EOC_CURRENT_PROGRAM_KEY
} from "../../../../mp-core/helpers/storage"
import { getAvailableProgramCourses } from "../../../../../services/ready-services/report/reportQueryResolver"
import {
  PracticeTestType,
  Program,
  ProgramAndCoursesInfo
} from "../../../../../types/gqlTypes/graphql"
import { getProgramByLicenseName } from "../../../../mp-core/helpers/getProgramByLicenseName"
import { hasValidLicenses } from "../../../../student-experience/helpers/validateStudentLicenses"
import { authingUserSuccess } from "../../../../mp-core/redux/modules/users"
import { useDispatch } from "react-redux"

export type ProgramsContextType = {
  currentProgram: string | null
  currentProgramSetter: (program: string) => void
  programsAndCourses: ProgramAndCoursesInfo[]
  programsAndCoursesLoading: boolean
  currentProgramCourse: ProgramAndCoursesInfo | null
  licensedPrograms: string[]
  programsAndCoursesMap: Map<Program, ProgramAndCoursesInfo>
  checkProgramType: (program: string, targetType: PracticeTestType) => boolean
  programHasOverall: (program: string) => boolean
  getDefaultCourse: (program: string, course?: string) => string
  validateAndGetProgram: (program: string) => string | null
  isValidProgram: (program: string) => boolean
  getFirstValidProgram: () => string | null
  getProgramDisplayName: (program: Program) => string
  setProgramDisplayNames: (subjects: string[]) => string[]
  hasLicensableProducts: boolean
}

// Create a default context value
const defaultContextValue: ProgramsContextType = {
  currentProgram: null,
  currentProgramSetter: () => {},
  programsAndCourses: [],
  programsAndCoursesMap: new Map([]),
  programsAndCoursesLoading: false,
  currentProgramCourse: null,
  licensedPrograms: [],
  checkProgramType: () => false,
  programHasOverall: () => false,
  getDefaultCourse: () => "overall",
  validateAndGetProgram: () => null,
  isValidProgram: () => false,
  getFirstValidProgram: () => null,
  getProgramDisplayName: () => null,
  setProgramDisplayNames: () => null,
  hasLicensableProducts: false
}

export const ProgramsContext =
  React.createContext<ProgramsContextType>(defaultContextValue)

const getLicenses = (user: User, licenses: any) => {
  const programLicenses = licenses?.filter(license => {
    if (!user) return false

    if (Roles.Teacher === user.role) {
      return Object.values(license?.licensedUsers ?? {}).length
    }

    if (
      Roles.Sales_Rep !== user.role &&
      Roles.Super_Administrator !== user.role
    ) {
      return !license.isExpired
    }

    return true
  })

  return programLicenses
    ?.map(item => {
      const program = getProgramByLicenseName(item.name)

      return {
        id: item.id,
        name: item.name,
        key: program,
        url:
          item.navCategory === "Study Plan"
            ? `/eoc/${program}`
            : getHomePath(user)
      }
    })
    .filter(item => item.key)
    .reduce((res, item) => {
      return {
        ...res,
        [item.key]: [item]
      }
    }, {})
}

interface Props {
  user: User
  isAuthed: boolean
  userLicenses: LicensesSet[]
}

export const ProgramsProvider: React.FC<Props> = ({
  user,
  isAuthed,
  userLicenses,
  children
}) => {
  const location = useLocation()
  const dispatch = useDispatch()

  const [programsAndCourses, setProgramsAndCourses] = useState<
    ProgramAndCoursesInfo[]
  >([])

  const [programsAndCoursesMap, setProgramsAndCoursesMap] = useState<
    Map<Program, ProgramAndCoursesInfo>
  >(new Map([]))

  const [programsAndCoursesLoading, setProgramsAndCoursesLoading] =
    useState<boolean>(false)

  const [currentProgram, setCurrentProgram] = useState<string>(null)

  const currentProgramCourse = programsAndCourses.find(
    programCourse =>
      programCourse.program.toUpperCase() === currentProgram?.toUpperCase()
  )

  const licensedPrograms = useMemo(
    () => Object.keys(getLicenses(user, userLicenses ?? []) || {}),
    [user, userLicenses]
  )

  const currentProgramSetter = useCallback((program: string) => {
    setCurrentProgram(program)
  }, [])

  const checkProgramType = useCallback(
    (program: string, targetType: PracticeTestType) => {
      const programCourse = programsAndCourses.find(
        programCourse =>
          programCourse.program.toUpperCase() === program?.toUpperCase()
      )

      return (
        programCourse?.courses?.[0]?.properties?.practiceTestType === targetType
      )
    },
    [programsAndCourses]
  )

  useEffect(() => {
    if (!user?.id || !isAuthed) return

    const fetchProgramsAndCourses = async () => {
      setProgramsAndCoursesLoading(true)

      try {
        const programsAndCourses = await getAvailableProgramCourses({
          licensedOnly: true
        })

        setProgramsAndCourses(
          [...programsAndCourses].sort((programCourse1, programCourse2) =>
            programCourse1.program.localeCompare(programCourse2.program)
          )
        )

        setProgramsAndCoursesMap(
          new Map<Program, ProgramAndCoursesInfo>(
            programsAndCourses.map(i => [i.program, i as ProgramAndCoursesInfo])
          )
        )
      } catch (error) {
        console.warn("[ProgramsContext]", error)
      } finally {
        setProgramsAndCoursesLoading(false)
      }
    }

    fetchProgramsAndCourses()
  }, [user?.id, isAuthed])

  // Setup current program from URL or session storage.
  useEffect(() => {
    if (!user) {
      return
    }

    if (location.pathname.includes("/teaching-tools/study-plan")) {
      const params = new URLSearchParams(location.search)
      const testParam = params.get("test")
      const [program, _subject] = testParam ? testParam.split("_") : []
      setCurrentProgram(program)
      return
    }

    const match = matchPath<{ program: string; page: string }>(
      location.pathname,
      {
        path: [
          "/eoc/:program",
          "/ebooks/:program",
          "/snap/teacher/elements/:program",
          "/:page/:module/:program"
        ]
      }
    )

    const urlProgram =
      match?.params.page !== "manage" ? match?.params?.program : ""

    if (urlProgram && licensedPrograms.includes(urlProgram)) {
      setCurrentProgram(urlProgram)
      return
    }

    const savedProgram =
      sessionStorage.getItem(EOC_CURRENT_PROGRAM_KEY)?.toLowerCase() ?? ""

    if (savedProgram && licensedPrograms.includes(savedProgram)) {
      setCurrentProgram(savedProgram)
    }
  }, [user, licensedPrograms, location])

  // Update session storage on current program change.
  useEffect(() => {
    const sessionStorageProgram = sessionStorage.getItem(
      EOC_CURRENT_PROGRAM_KEY
    )

    if (!currentProgram || sessionStorageProgram === currentProgram) return

    sessionStorage.setItem(
      EOC_CURRENT_PROGRAM_KEY,
      currentProgram?.toUpperCase()
    )
    sessionStorage.setItem(CURRENT_PROGRAM_KEY, currentProgram)
  }, [currentProgram])

  useEffect(() => {
    if (
      !programsAndCourses?.length ||
      !licensedPrograms?.length ||
      !currentProgram
    )
      return

    if (
      !!currentProgramCourse?.program ||
      !!licensedPrograms.includes(currentProgram)
    )
      return

    const firstAvailableProgram = programsAndCourses[0].program

    if (!firstAvailableProgram) return

    setCurrentProgram(firstAvailableProgram.toLowerCase())
  }, [
    currentProgram,
    currentProgramCourse?.program,
    programsAndCourses,
    licensedPrograms
  ])

  // Helper function to check if a program has "overall" option (non-EOC programs)
  const programHasOverall = useCallback(
    (program: string): boolean => {
      if (import.meta.env.VITE_APPLICATION_ID === "workkeys") {
        return false
      }

      const programInfo = programsAndCourses.find(
        p => p.program.toLowerCase() === program.toLowerCase()
      )
      return Boolean(programInfo && programInfo.programInfo?.testType !== "EOC")
    },
    [programsAndCourses]
  )

  // Helper function to get the default course for a program
  // If course is provided, validates it exists for the program, otherwise returns default
  const getDefaultCourse = useCallback(
    (program: string, course?: string): string => {
      const programInfo = programsAndCourses.find(
        p => p.program.toLowerCase() === program.toLowerCase()
      )

      if (!programInfo) return ""

      // If a course is provided, check if it's valid for this program
      if (course) {
        // Check if course is "overall" and program supports it
        if (
          course.toLowerCase() === "overall" &&
          programInfo.programInfo?.testType !== "EOC"
        ) {
          return course
        }

        // Check if the course exists in the program's courses
        const courseExists = programInfo.courses?.some(
          c => c.name?.toLowerCase() === course.toLowerCase()
        )

        if (courseExists) {
          return course
        }
      }

      // Course not valid or not provided, return default
      // Non-EOC programs default to "overall"
      if (programInfo.programInfo?.testType !== "EOC") {
        return "overall"
      }

      // EOC programs default to first available course
      return programInfo.courses?.[0]?.name || "overall"
    },
    [programsAndCourses]
  )

  const getProgramDisplayName = useCallback(
    (program: Program) => {
      return (
        programsAndCoursesMap.get(program)?.programInfo?.displayName || program
      )
    },
    [programsAndCoursesMap]
  )

  const setProgramDisplayNames = (subjects: string[]) => {
    return subjects.map(subject => {
      const subjectArray = subject.trim().split(" ")
      const program = (subjectArray?.[0] ?? "") as Program

      const displayName = getProgramDisplayName(program)

      subjectArray[0] = displayName

      return subjectArray.join(" ")
    })
  }

  // Helper function to check if a program is valid (exists in user's available programs)
  const isValidProgram = useCallback(
    (program: string): boolean => {
      if (!program) return false

      // Check if the program exists in the user's available programs
      return programsAndCourses.some(
        p => p.program.toLowerCase() === program.toLowerCase()
      )
    },
    [programsAndCourses]
  )

  // Helper function to get the first valid program
  const getFirstValidProgram = useCallback((): string | null => {
    return programsAndCourses[0]?.program || null
  }, [programsAndCourses])

  // Helper function to validate a program and return it or the first valid program
  const validateAndGetProgram = useCallback(
    (program: string): string | null => {
      if (!program) return getFirstValidProgram()

      // Return the program if valid, otherwise return first available
      return isValidProgram(program) ? program : getFirstValidProgram()
    },
    [isValidProgram, getFirstValidProgram]
  )

  // Calculate if user has licensable products
  // For students, this checks if they have any valid licenses
  // For non-students, always true (no restriction)
  const hasLicensableProducts = useMemo(() => {
    // Non-students always have access
    if (user?.role !== "Student") {
      return true
    }

    // For students, check if they have any valid licenses using the same logic as NoCoursesState
    return hasValidLicenses(userLicenses)
  }, [user?.role, userLicenses])

  return (
    <ProgramsContext.Provider
      value={{
        currentProgram,
        currentProgramSetter,
        programsAndCourses,
        programsAndCoursesMap,
        programsAndCoursesLoading,
        currentProgramCourse,
        licensedPrograms,
        checkProgramType,
        programHasOverall,
        getDefaultCourse,
        validateAndGetProgram,
        isValidProgram,
        getFirstValidProgram,
        getProgramDisplayName,
        setProgramDisplayNames,
        hasLicensableProducts
      }}
    >
      {children}
    </ProgramsContext.Provider>
  )
}
